import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, Fragment } from 'react';
import {
  Card,
  Checkbox,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  Chip
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { BiCloudDownload } from 'react-icons/bi';
import CloseIcon from '@mui/icons-material/Close';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateNew, fDateTime2 } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import RevenueToolbar from './RevenueToolbar';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import {
  getMisMatchedDownloadList,
  getRevenueGridList
} from '../../store/Slice/Revenue/RevenueSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import {
  getBankDropdownData,
  getLoanTypeData,
  getMasterListType
} from '../../store/Slice/Global/GlobalSlice';
import { isValidValue, transformToSelectOptions } from '../../utils/CommonFunction';
import useRevenueSelector from '../../store/Slice/Revenue/RevenueSelector';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

export default function RevenueList() {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const [page1, setPage1] = useState(1);
  const [selected, setSelected] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [filterDatesRange, setFilterDatesRange] = useState([
    { startDate: null, endDate: null, key: 'selection' }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    loanTypeId: null,
    bankId: null,
    branchId: null,
    misIsMatch: null,
    misIsMatchId: null
  });
  const [fileTypeId, setFileTypeId] = useState(null);
  const [masterTypeOptions, setMasterTypeOptions] = useState({
    fileStatusOptions: [],
    statusOptions: []
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const debouncedSearch = useDebounce(filterName, 600);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { RevenueListGridData } = useRevenueSelector();

  const handleDownloadMISDetails = async () => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      // accessLevelId: LoginData?.accessLevelId,
      // entityListRequestModel: {},
      // revenueFileStatusId: 260,
      fileFormat: 'csv'
    };

    const res = await dispatch(getMisMatchedDownloadList(credentials));
    // console.log('RevenueListGridData2', res);
    if (!res || !res.success || !res.data) {
      console.error('❌ No CSV data found!');
      return;
    }

    const csvText = String(res.data); // 👈 ensure it's a string
    try {
      const blob = new Blob([`\uFEFF${csvText}`], {
        type: 'text/csv;charset=utf-8;'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RevenueMisMatchReport.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadPopup(false);
    }
  };

  // === Fetch Revenue List ===
  const getRevenueListApi = useCallback(() => {
    if (!fileTypeId) return;

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      filterModel: {
        bankId: filterParameter?.bankId || null,
        branchId: filterParameter?.branchId || null,
        loanTypeId: filterParameter?.loanTypeId || null,
        misIsMatch: isValidValue(filterParameter?.misIsMatch)
          ? filterParameter?.misIsMatch === 'Matched'
          : null,
        fromDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null
      },
      revenueFileStatusId: fileTypeId
    };

    dispatch(getRevenueGridList(credentials));
  }, [
    dispatch,
    LoginData,
    debouncedSearch,
    order,
    filterDatesRange,
    fileTypeId,
    filterParameter,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    getRevenueListApi();
  }, [getRevenueListApi]);

  // === Master Data (FileType, Status, Bank, Loan) ===
  const MasterTypeApi = async () => {
    const [statusType, fileType] = ['REVENUE_STATUS_TYPE', 'REVENUE_FILE_STATUS_TYPE'];
    const [statusTypeRes, fileRes] = await Promise.all([
      dispatch(getMasterListType(statusType)),
      dispatch(getMasterListType(fileType))
    ]);

    const statusOptions = transformToSelectOptions(
      statusTypeRes?.data?.masterTypes || [],
      'typeName',
      'typeId'
    );
    const fileStatusOptions = transformToSelectOptions(
      fileRes?.data?.masterTypes || [],
      'typeName',
      'typeId'
    );

    setFileTypeId(fileStatusOptions?.[0]?.value);
    setMasterTypeOptions({ fileStatusOptions, statusOptions });
  };

  const getLoanMasterApi = useCallback(() => {
    const obj = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getLoanTypeData(obj));
  }, [dispatch, LoginData]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId, isWithCode: true };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    MasterTypeApi();
    getBankDropApi();
    getLoanMasterApi();
  }, []);

  // === Common Handlers ===
  const handleRequestSort = (_, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(RevenueListGridData);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (e) => setFilterName(e.target.value);

  const handleClick = (_, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = [...selected, name];
    else newSelected = selected.filter((_, idx) => idx !== selectedIndex);
    setSelected(newSelected);
  };

  const handleChangeTab = (_, newValue) => {
    setFileTypeId(newValue);
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setFilterParameter({ loanTypeId: null, bankId: null, branchId: null, misIsMatch: null });
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - RevenueListGridData.length) : 0;

  // === Generate Columns Dynamically per File Type ===
  const getTableColumns = (typeName) => {
    const dateColumn = typeName?.toLowerCase().includes('receipt')
      ? { id: 'fileRequestDate', label: 'Request Date' }
      : typeName?.toLowerCase().includes('approved')
      ? { id: 'fileApprovalDate', label: 'Approval Date' }
      : { id: 'fileDisbursedDate', label: 'Disbursed Date' };

    const amountColumn = typeName?.toLowerCase().includes('disbursed')
      ? { id: 'loanAmountDisbursed', label: 'Disbursed Amount' }
      : { id: 'loanAmountRequested', label: 'Loan Amount' };

    return [
      { id: 'bankName', label: 'Bank Name' },
      { id: 'fileNumber', label: 'File No' },
      { id: 'branchName', label: 'Branch Name' },
      dateColumn,
      { id: 'borrowerName', label: 'Borrower Name' },
      { id: 'empClass', label: 'Emp Class' },
      { id: 'loanTypeName', label: 'Loan Product Type' },
      amountColumn,
      { id: 'misMonth', label: 'MIS Month' },
      { id: 'misStatus', label: 'MIS Status' },
      { id: 'action', label: 'ACTION' }
    ];
  };

  useEffect(() => {
    if (!RevenueListGridData) {
      setHasNextPage(true);
      return;
    }
    // If API returned fewer rows → last page
    setHasNextPage(RevenueListGridData.length >= rowsPerPage);
  }, [RevenueListGridData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  // Clear selected items when list data changes
  useEffect(() => {
    setSelected([]);
  }, [RevenueListGridData]);

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Bank MIS Detail" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* === Tabs === */}
            <Tabs
              value={fileTypeId}
              onChange={handleChangeTab}
              className="tabOuter"
              aria-label="revenue tabs"
              classes={{ indicator: 'tabIndicator' }}
            >
              {masterTypeOptions.fileStatusOptions.map((item) => (
                <Tab
                  key={item.value}
                  label={item.label}
                  value={item.value}
                  classes={{ selected: 'tabSelected', root: 'tabDefault' }}
                />
              ))}
            </Tabs>
            <Tooltip title="Download Mismatch Report" placement="top" arrow>
              <IconButton
                classes={{ disabled: 'disabledBtn' }}
                className="squareIconButton"
                onClick={(e) => setDownloadPopup(true)}
              >
                <BiCloudDownload size={18} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* === Tab Panels (Dynamic) === */}
          {masterTypeOptions.fileStatusOptions.map((tab) => (
            <TabPanel key={tab.value} value={fileTypeId} index={tab.value}>
              <Card>
                <RevenueToolbar
                  parent="revenue"
                  children={tab.label.toLowerCase()}
                  selected={selected}
                  setSelected={setSelected}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  filterDatesRange={filterDatesRange}
                  setFilterDatesRange={setFilterDatesRange}
                  masterTypeOptions={masterTypeOptions}
                  setFilterParameter={setFilterParameter}
                  filterParameter={filterParameter}
                />

                <Scrollbar>
                  <TableContainer sx={{ minWidth: 800, minHeight: '50vh', overflow: 'auto' }}>
                    <Table>
                      <UserListHead
                        order={order}
                        orderBy={orderBy}
                        headLabel={getTableColumns(tab.label)}
                        rowCount={RevenueListGridData.length}
                        numSelected={selected.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                        isCheckBox="true"
                      />

                      <TableBody>
                        {RevenueListGridData.length > 0 ? (
                          RevenueListGridData.map((item, index) => {
                            const isItemSelected = selected.indexOf(item) !== -1;
                            return (
                              <StyledTableRow
                                hover
                                key={index}
                                selected={isItemSelected}
                                aria-checked={isItemSelected}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isItemSelected}
                                    onChange={(e) => handleClick(e, item)}
                                  />
                                </TableCell>

                                {getTableColumns(tab.label).map((col) => (
                                  <StyledTableCell key={col.id} align="left">
                                    {col.id === 'action' ? (
                                      <UserMoreMenu
                                        revenueId={item?.revenueId}
                                        leadId={item?.leadId}
                                        applicationId={item?.applicationId}
                                        rowId={item}
                                        parent={`revenue-${tab.label.toLowerCase()}`}
                                      />
                                    ) : col.id === 'loanAmountDisbursed' &&
                                      item?.misStatus === 'Matched' ? (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}
                                      >
                                        {item[col.id]}

                                        <Tooltip
                                          title={item?.payoutDisbursementAmount}
                                          placement="left"
                                          arrow
                                        >
                                          <IconButton
                                            size="small"
                                            sx={{
                                              padding: '2px',
                                              fontSize: 14
                                            }}
                                          >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </span>
                                    ) : item[col.id] ? (
                                      col.id.includes('Date') ? (
                                        fDateNew(item[col.id])
                                      ) : (
                                        item[col.id]
                                      )
                                    ) : (
                                      NOT_AVAILABLE
                                    )}
                                  </StyledTableCell>
                                ))}
                              </StyledTableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <StyledTableCell align="center" colSpan={11} sx={{ py: 3 }}>
                              <SearchNotFound searchQuery={filterName} />
                            </StyledTableCell>
                          </TableRow>
                        )}

                        {/* {emptyRows > 0 && (
                              // <TableRow style={{ height: 53 * emptyRows }}>
                              //   <StyledTableCell colSpan={6} />
                              // </TableRow>
                            )} */}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              </Card>
              <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
            </TabPanel>
          ))}
        </Box>

        {/* Download pop up start */}
        <Dialog
          className="PopupBox"
          open={downloadPopup}
          onClose={(e) => setDownloadPopup(false)}
          fullWidth
        >
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Download</Typography>
            <IconButton onClick={(e) => setDownloadPopup(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              Are you sure you want to download MisMatch Details Report?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDownloadMISDetails} variant="contained" className="mainBtn">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}

// ✅ Generic TabPanel Component
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && (
        <Box sx={{ paddingTop: '1rem' }}>
          <Card>{children}</Card>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};
